import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import GridItem from '../../../components/Grid/GridItem.js';
import GridContainer from '../../../components/Grid/GridContainer.js';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { Card, CardContent, CircularProgress, Box, Typography } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAllGuides,
  createGuide,
  updateGuide,
  deleteGuide,
  getAllDiscords,
  Guide,
} from './GuideApi';
import { GuideForm } from './GuideForm';
import GuidesTable, { Guide as TableGuide } from './GuidesTable';
import toast from 'react-hot-toast';
import { Navigate } from 'react-router-dom';
import { RequireAuth } from 'components/Auth/RequireAuth';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    '&&&&': {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      margin: '0 auto',
      width: 'auto',
      maxWidth: '100%',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  headLineStyle: {
    fontWeight: '500',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
  },
  contentContainer: {
    width: '100%',
    color: theme.palette.text.primary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  formSection: {
    marginTop: theme.spacing(4),
  },
}));

function GuidesPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  useDocumentTitle(t('navigation.guides'));
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  const [editingGuide, setEditingGuide] = useState<Guide | undefined>(undefined);

  // Query for fetching all guides
  const {
    data: guides,
    isLoading: isLoadingGuides,
    isError: isGuidesError,
  } = useQuery({
    queryKey: ['guides'],
    queryFn: getAllGuides,
  });

  // Query for fetching all Discord teams
  const {
    data: discords,
    isLoading: isLoadingDiscords,
    isError: isDiscordsError,
  } = useQuery({
    queryKey: ['discords'],
    queryFn: getAllDiscords,
  });

  // Mutation for creating a new guide
  const createGuideMutation = useMutation({
    mutationFn: createGuide,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guides'] });
      toast.success(t('guides.messages.createSuccess'));
      setEditingGuide(undefined);
    },
    onError: (error) => {
      console.error('Error creating guide:', error);
      toast.error(t('guides.messages.createError'));
    },
  });

  // Mutation for updating an existing guide
  const updateGuideMutation = useMutation({
    mutationFn: (guide: Guide | FormData) => {
      // Check if it's FormData (for file uploads)
      if (guide instanceof FormData) {
        const id = guide.get('id') as string;
        if (!id) throw new Error('Guide ID is required for update');
        return updateGuide(id, guide);
      }

      // Regular object update
      if (!guide.id) throw new Error('Guide ID is required for update');
      return updateGuide(guide.id, guide);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guides'] });
      toast.success(t('guides.messages.updateSuccess'));
      setEditingGuide(undefined);
    },
    onError: (error) => {
      console.error('Error updating guide:', error);
      toast.error(t('guides.messages.updateError'));
    },
  });

  // Mutation for deleting a guide
  const deleteGuideMutation = useMutation({
    mutationFn: deleteGuide,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guides'] });
      toast.success(t('guides.messages.deleteSuccess'));
    },
    onError: (error) => {
      console.error('Error deleting guide:', error);
      toast.error(t('guides.messages.deleteError'));
    },
  });

  // Mutation for toggling guide published status
  const togglePublishedMutation = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) => {
      return updateGuide(id, { published });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guides'] });
      toast.success(t('guides.messages.updateSuccess'));
    },
    onError: (error) => {
      console.error('Error updating guide publish status:', error);
      toast.error(t('guides.messages.updateError'));
    },
  });

  const handleEdit = useCallback((guide: Guide) => {
    setEditingGuide(guide);
    // Scroll to form
    setTimeout(() => {
      document.getElementById('guide-form-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleDelete = useCallback(
    (id: string) => {
      deleteGuideMutation.mutate(id);
    },
    [deleteGuideMutation]
  );

  const handleTogglePublished = useCallback(
    (id: string, published: boolean) => {
      togglePublishedMutation.mutate({ id, published });
    },
    [togglePublishedMutation]
  );

  const handleFormSubmit = useCallback(
    (guide: Guide | FormData) => {
      if (guide instanceof FormData) {
        // For FormData (file uploads)
        const guideId = guide.get('id') as string | null;

        if (guideId && editingGuide) {
          // Update existing guide
          updateGuideMutation.mutate(guide);
        } else {
          // Create new guide
          createGuideMutation.mutate(guide);
        }
      } else {
        // For regular Guide object
        if (editingGuide && editingGuide.id) {
          updateGuideMutation.mutate({ ...guide, id: editingGuide.id });
        } else {
          createGuideMutation.mutate(guide);
        }
      }
    },
    [editingGuide, updateGuideMutation, createGuideMutation]
  );

  const handleFormCancel = useCallback(() => {
    setEditingGuide(undefined);
  }, []);

  // Navigate to login if not authenticated
  if (!sessionToken) {
    return <Navigate to="/login" replace />;
  }

  // Map guides from API format to GuidesTable format
  const mapGuidesToTableFormat =
    guides?.map((guide) => ({
      id: guide.id,
      name: guide.name,
      type: guide.type.toString(),
      characterClass: guide.class.toString(),
      teamId: guide.team_id,
      teamName: guide.team_name,
      createdAt: guide.created_at,
      updatedAt: guide.updated_at,
      published: guide.published,
    })) || [];

  // Find the original guide from the API data for editing
  const handleEditTable = (tableGuide: TableGuide) => {
    const originalGuide = guides?.find((g) => g.id === tableGuide.id);
    if (originalGuide) {
      handleEdit(originalGuide);
    }
  };

  const isLoading = isLoadingGuides || isLoadingDiscords;
  const isError = isGuidesError || isDiscordsError;
  const isAdmin = true; // You might want to control this with a permission check

  return (
    <RequireAuth requiredPermission="is_staff">
      {isLoading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Card>
          <CardContent>
            <Typography color="error">{t('common.errorLoadingData')}</Typography>
          </CardContent>
        </Card>
      ) : (
        <GridContainer justify="center">
          {/* Guides Table Section */}
          <GridItem xs={12} sm={12} md={12}>
            <GuidesTable
              guides={mapGuidesToTableFormat}
              discords={discords || []}
              onEditGuide={handleEditTable}
              onDeleteGuide={handleDelete}
              onTogglePublished={handleTogglePublished}
              isAdmin={isAdmin}
            />
          </GridItem>

          {/* Guide Form Section */}
          <GridItem xs={12} sm={12} md={12} id="guide-form-section">
            <GuideForm
              guide={editingGuide}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              loading={createGuideMutation.isPending || updateGuideMutation.isPending}
            />
          </GridItem>
        </GridContainer>
      )}
    </RequireAuth>
  );
}

export { GuidesPage };
